// ─────────────────────────────────────────────────────────────────────────────
// What counts as actually reading a story.
//
// minReadSeconds is mirrored in SQL by classroom_min_read_seconds()
// (20261026_reading_pace.sql), which is what REFUSES a pass that is too quick.
// Change both together, or the page will offer a "Finish" button the server
// then rejects.
//
// The pace is the teacher's: each story has its own words-a-minute
// (assignments.read_wpm), and each student a share of that time
// (classroom_members.read_time_pct), so faster readers can be let off sooner.
//
// The per-paragraph rules are client-only. The server cannot see the page, so it
// checks the total; the page decides which seconds ARE reading, and only those
// are ever sent (useReadingCredit in AssignmentView.tsx). A second counts when:
//   - the page is held still (scrolling up and down earns nothing), and
//   - the paragraph being credited is the next unread one, mostly on screen
//     (paragraphs are read in order: parking further down earns nothing), or
//     the student is going back over one already read.
// Each paragraph needs its own share of the reading time, so the time has to be
// spent across the whole story, not at the top and then flicked to the end.
// ─────────────────────────────────────────────────────────────────────────────

/** Words a minute a story is timed at, slowest (most time) first. */
export const PACES = [
  { wpm: 40, label: 'Very slow', note: 'beginners, or a hard story' },
  { wpm: 55, label: 'Slow', note: 'careful, word by word' },
  { wpm: 70, label: 'Steady', note: 'the default' },
  { wpm: 90, label: 'Brisk', note: 'confident readers' },
  { wpm: 110, label: 'Quick', note: 'strong readers' },
  { wpm: 140, label: 'Fluent', note: 'near-native pace' },
] as const;
export const DEFAULT_WPM = 70;

/** A student's share of a story's reading time (classroom_members.read_time_pct). */
export const TIME_SHARES = [
  { pct: 150, label: 'Needs more time (150%)' },
  { pct: 125, label: 'A bit more time (125%)' },
  { pct: 100, label: 'Standard (100%)' },
  { pct: 85, label: 'Faster reader (85%)' },
  { pct: 70, label: 'Fast reader (70%)' },
  { pct: 50, label: 'Very fast reader (50%)' },
] as const;

export const MIN_READ_SECONDS = 60;

/** Each paragraph needs this share of its own reading time. */
export const PARAGRAPH_SHARE = 1;
export const PARAGRAPH_MIN_SECONDS = 3;

/** Still enough to be reading: over the last STILL_WINDOW_MS the page moved
 *  less than this share of the screen's height, all movement added up (so a
 *  slow back-and-forth adds up too). Readers scroll a little, and in bursts. */
export const STILL_WINDOW_MS = 2_000;
export const STILL_MAX_TRAVEL = 0.35;

/** Reading stops counting after this long with no scroll, tap, key or mouse movement. */
export const READING_IDLE_MS = 45_000;

export const paceLabel = (wpm: number) => PACES.find(p => p.wpm === wpm)?.label ?? `${wpm} words a minute`;

export const wordCount = (text: string) => text.trim().split(/\s+/).filter(Boolean).length;

export function minReadSeconds(text: string, wpm = DEFAULT_WPM, timePct = 100): number {
  return Math.ceil(Math.max(MIN_READ_SECONDS, wordCount(text) * 60 / Math.max(wpm, 20)) * Math.max(timePct, 25) / 100);
}

export function paragraphMinSeconds(words: number, wpm = DEFAULT_WPM, timePct = 100): number {
  return Math.max(PARAGRAPH_MIN_SECONDS, Math.ceil(words * 60 / Math.max(wpm, 20) * PARAGRAPH_SHARE * timePct / 100));
}

/** Paragraphs exactly as TranslatableText splits them. */
export function paragraphs(text: string): string[] {
  return text.replace(/\r\n/g, '\n').split(/\n{2,}/);
}

export function clock(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}
